import {
  CircularProgress,
  Menu,
  MenuItem,
  SvgIcon,
  Tooltip,
  useTheme,
} from "@mui/material";
import { t } from "i18next";
import Q3DIconButton from "../../Parts/Q3DIconButton";
import { useContext, useEffect, useRef, useState } from "react";
import {
  toolBoxIcon_1,
  BLACK,
  toolBoxIcon_2,
  WHITE,
  toolBoxIcon_3,
} from "../../config/componentsConfig";
import {
  DETAIL_TAB,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import RouteIcon from "@mui/icons-material/Route";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import GrainIcon from "@mui/icons-material/Grain";
import ToolIcon from "../../../components/Icon";
import CoordinateIcon from "../../../../images/icons/neutral_edit/Coordinate.svg";
import ImageViewerDialog from "../../PhotoshotDialog";
import { config } from "../../../config/config";
import { Common } from "../../../../common/src";
import DataDesign from "../../../../images/icons/cubes.svg";
import { projectARFileApis } from "../../../api/project";
import { observe } from "../../../utils/observe";
import { devLog } from "../../../utility/devLog";

type Props = {
  projectDetail: Common.ProjectDetail;
  onChangeIfcState?: (isCentered: boolean) => void;
};

/** Extensions belonging to the model-3D group (converted to SCM server-side). */
const MODEL3D_EXTENSIONS = [".dwg", ".fbx", ".glb"];

function isModel3DFileName(fileName?: string | null): boolean {
  if (!fileName) return false;
  const lower = fileName.toLowerCase();
  return MODEL3D_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Old group (IFC / LandXML / …). `ifc_file` is authoritative; `design_file`
 * is a legacy catch-all written for EVERY design upload — including
 * DWG/FBX/GLB — so it only counts here when it is not a model-3D file.
 */
function getOldDesignFileName(
  projectDetail: Common.ProjectDetail,
): string | null {
  if (projectDetail?.ifc_file) return projectDetail.ifc_file;
  if (
    projectDetail?.design_file &&
    !isModel3DFileName(projectDetail.design_file)
  )
    return projectDetail.design_file;
  return null;
}

/** Model-3D group (FBX/GLB/DWG → SCM). */
function getModel3DFileName(
  projectDetail: Common.ProjectDetail,
): string | null {
  return (projectDetail as any)?.scm_file || null;
}

/**
 * `useViDoc` is an IFC-specific gate (only projects uploaded via the ViDoc
 * pipeline have IFC). SCM projects don't go through that pipeline, so the
 * model-3D lane must not be blocked on it.
 */
function canLoadOldDesign(projectDetail: Common.ProjectDetail): boolean {
  if (!getOldDesignFileName(projectDetail)) return false;
  return projectDetail.useViDoc !== false;
}

function canLoadModel3D(projectDetail: Common.ProjectDetail): boolean {
  return !!getModel3DFileName(projectDetail);
}

/** DWG/FBX/GLB are converted to SCM asynchronously (Lambda) after upload. */
function isModel3DConverting(projectDetail: Common.ProjectDetail): boolean {
  const status = (projectDetail as any)?.scm_status;
  return status === "pending" || status === "converting";
}

function isModel3DFailed(projectDetail: Common.ProjectDetail): boolean {
  return (projectDetail as any)?.scm_status === "failed";
}

/** Model-3D data is only displayable once its conversion finished cleanly. */
function canShowModel3D(projectDetail: Common.ProjectDetail): boolean {
  return (
    canLoadModel3D(projectDetail) &&
    !isModel3DConverting(projectDetail) &&
    !isModel3DFailed(projectDetail)
  );
}

export const ShowToolMenu = (props: Props) => {
  const theme = useTheme();

  const [visibilityAnchorEl, setVisibilityAnchorEl] = useState<Element>(null); // menu anchor
  const [showAxis, setShowAxis] = useState<boolean>(true); // axis
  const [showPointCloud, setShowPointCloud] = useState<boolean>(true); // pointCloud
  const [showPhotoshot, setShowPhotoshot] = useState<boolean>(true); // photoshot
  // BR-6: when both groups exist only the old (IFC) one auto-shows. Computed
  // in the state initialiser so the first paint is already correct — doing it
  // in an effect would briefly show both lanes.
  const [showOldDesign, setShowOldDesign] = useState<boolean>(() =>
    canLoadOldDesign(props.projectDetail),
  );
  const [showModel3D, setShowModel3D] = useState<boolean>(
    () =>
      !canLoadOldDesign(props.projectDetail) &&
      canShowModel3D(props.projectDetail),
  );
  const [oldDesignLoading, setOldDesignLoading] = useState<boolean>(false);
  const [model3DLoading, setModel3DLoading] = useState<boolean>(false);
  const oldDesignUrlRef = useRef<string | null>(null);
  const model3DUrlRef = useRef<string | null>(null);

  const [showDxf, setShowDxf] = useState<boolean>(false); // DXF
  const [dxfLoading, setDxfLoading] = useState<boolean>(false); // DXF loading state
  const [gsplatLoading, setGsplatLoading] = useState<boolean>(false); // GS loading state
  const dxfFileUrlRef = useRef<string | null>(null);
  const [dxfBlendEnabled, setDxfBlendEnabled] = useState<boolean>(false); // DXF blend
  const openVisibilityMenu = (event: any) => {
    setVisibilityAnchorEl(event.currentTarget);
  };
  const {
    viewer,
    openProjectState,
    setOpenProjectState,
    detailTab,
    projectDetail,
    isArView,
    showGsplat,
    setShowGsplat,
    setShowIfcPutObject,
    updateCurrentViewerMode,
  } = useContext(ProjectDetailContext);
  const btnSize = "36px";

  const closeVisibilityMenu = () => {
    setVisibilityAnchorEl(null);
  };

  const toggleAxisVisiblity = () => {
    setShowAxis(!showAxis);
    viewer.setAxisVisiblity(!showAxis);
  };

  const toggleOldDesign = () => {
    setShowOldDesign((visible) => !visible);
  };

  const toggleModel3D = () => {
    setShowModel3D((visible) => !visible);
  };

  /** Design data only renders on the SfM tab and in AR view. */
  const isDesignViewable = isArView || detailTab === DETAIL_TAB.SFM;

  /**
   * The model-3D toggle explains WHY it is unavailable while its DWG/FBX/GLB
   * source is still being converted, or when that conversion failed.
   */
  const model3DStatusMessage = isModel3DConverting(projectDetail)
    ? t("CONVERTING_DWG_TO_SCM")
    : isModel3DFailed(projectDetail)
      ? t("DWG_CONVERT_FAILED")
      : t("DISPLAY_DESIGN_DATA_MODEL3D");

  // Start from a clean slate so a previously opened project's design data
  // does not linger in the shared viewer instance.

  useEffect(() => {
    viewer.designURL = null;
    viewer.scmURL = null;
    oldDesignUrlRef.current = null;
    model3DUrlRef.current = null;
  }, []);

  // Drop a lane when its file disappears from the project
  useEffect(() => {
    devLog("ShowToolMenu.projectDetail-effect", {
      projectId: props.projectDetail.id,
      isArView,
      las_file: props.projectDetail.las_file || null,
      ifc_file: props.projectDetail.ifc_file || null,
      design_file: props.projectDetail.design_file || null,
      scm_file: (props.projectDetail as any).scm_file || null,
      scm_status: (props.projectDetail as any).scm_status || null,
      useViDoc: props.projectDetail.useViDoc,
    });

    if (oldDesignUrlRef.current && !getOldDesignFileName(props.projectDetail)) {
      oldDesignUrlRef.current = null;
      setShowOldDesign(false);
      viewer.designURL = null;
    }

    if (model3DUrlRef.current && !getModel3DFileName(props.projectDetail)) {
      model3DUrlRef.current = null;
      setShowModel3D(false);
      viewer.scmURL = null;
    }

    if (isArView && !props.projectDetail.las_file) {
      setShowPointCloud(false);
      viewer.setPointCloudVisibility(false);
    }

    if (isArView && !props.projectDetail.gsx_file) {
      setShowGsplat(false);
    }

    if (
      isArView &&
      !props.projectDetail.las_file &&
      !props.projectDetail.ifc_file &&
      !!props.projectDetail.gsx_file
    ) {
      setShowGsplat(true);
    }
  }, [props.projectDetail]);

  /**
   * Each lane fetches its presigned URL lazily — the first time it actually
   * needs to be shown — and keeps it. Toggling afterwards only flips the
   * viewer's per-lane visible flag, so nothing is re-downloaded or re-parsed.
   */
  useEffect(() => {
    const fetchLane = async (
      enabled: boolean,
      canLoad: boolean,
      urlRef: React.MutableRefObject<string | null>,
      fileType: Common.AR_FILE_ACCEPTED_TYPE,
      assignToViewer: (url: string) => void,
    ) => {
      if (
        !enabled ||
        urlRef.current ||
        !isDesignViewable ||
        !projectDetail ||
        !canLoad
      ) {
        return;
      }

      try {
        devLog("fetchDesignLane.request", {
          fileType,
          projectId: projectDetail.id,
        });
        const response = await projectARFileApis.getFileUrl.get(
          projectDetail.id,
          fileType,
        );
        urlRef.current = response.data;
        assignToViewer(response.data);
      } catch (error) {
        console.error(`Failed to load ${fileType} design file:`, error);
        devLog("fetchDesignLane.error", { fileType, error: String(error) });
      }
    };

    fetchLane(
      showOldDesign,
      canLoadOldDesign(projectDetail),
      oldDesignUrlRef,
      Common.AR_FILE_ACCEPTED_TYPE.IFC,
      (url) => {
        viewer.designURL = url;
      },
    );

    fetchLane(
      showModel3D,
      canShowModel3D(projectDetail),
      model3DUrlRef,
      Common.AR_FILE_ACCEPTED_TYPE.SCM,
      (url) => {
        viewer.scmURL = url;
      },
    );
  }, [showOldDesign, showModel3D, projectDetail?.id, isDesignViewable]);

  // Per-lane show/hide — cheap flags, no dispose/reload
  useEffect(() => {
    viewer.designVisible = showOldDesign && isDesignViewable;
    viewer.scmVisible = showModel3D && isDesignViewable;
    // ToolBar's camera-centring hook still takes a single boolean: fire it
    // when EITHER lane is on, preserving the existing contract.
    props.onChangeIfcState &&
      props.onChangeIfcState(showOldDesign || showModel3D);
  }, [showOldDesign, showModel3D, isDesignViewable]);

  // DXF: fetch URL when toggled on (no SFM guard)
  useEffect(() => {
    const fetchDxf = async () => {
      if (!showDxf || dxfFileUrlRef.current || !projectDetail) return;

      try {
        const res = await projectARFileApis.getFileUrl.get(
          projectDetail.id,
          "DXF",
        );
        const url = typeof res.data === "object" ? res.data.url : res.data;
        if (url) {
          dxfFileUrlRef.current = url;
          viewer.dxfURL = url;
        }
      } catch (e) {
        console.error("[DXF] Failed to fetch DXF URL:", e);
      }
    };

    fetchDxf();
  }, [showDxf, projectDetail.id]);

  // DXF: sync overlay visibility with showDxf. On first mount, ADOPT the
  // viewer's current DXF state (the viewer singleton survives React remounts)
  // instead of clobbering it to null - this is what made the overlay silently
  // turn off when the toolbar remounted (tab/project switch). Guards avoid
  // re-assigning dxfURL to the same value, which would otherwise re-fire the
  // observer and reload/reset the DXF (observe() fires on every set).
  const dxfAdoptedRef = useRef(false);
  useEffect(() => {
    if (!dxfAdoptedRef.current) {
      dxfAdoptedRef.current = true;
      if (viewer.dxfURL) {
        dxfFileUrlRef.current = viewer.dxfURL;
        setShowDxf(true);
        if (viewer.dxfBlendEnabled) setDxfBlendEnabled(true);
        return;
      }
    }
    if (showDxf) {
      // Edge-blend controls removed: always colorize at full opacity.
      viewer.dxfBlendOpacity = 1;
      if (!viewer.dxfBlendEnabled) viewer.dxfBlendEnabled = true;
      if (!dxfBlendEnabled) setDxfBlendEnabled(true);
      if (dxfFileUrlRef.current && viewer.dxfURL !== dxfFileUrlRef.current) {
        viewer.dxfURL = dxfFileUrlRef.current;
      }
    } else {
      if (viewer.dxfURL !== null) viewer.dxfURL = null;
      // Reset blend when hiding DXF
      if (dxfBlendEnabled) {
        setDxfBlendEnabled(false);
        viewer.dxfBlendEnabled = false;
      }
    }
  }, [showDxf, dxfBlendEnabled]);

  // Hide the DXF overlay and re-render the point cloud when the DXF file is
  // deleted (directly or together with the point cloud).
  useEffect(() => {
    if (!props.projectDetail.dxf_file && dxfFileUrlRef.current) {
      dxfFileUrlRef.current = null;
      setShowDxf(false);
      setDxfBlendEnabled(false);
      if (viewer.dxfBlendEnabled) viewer.dxfBlendEnabled = false;
      if (viewer.dxfURL !== null) viewer.dxfURL = null;
    }
  }, [props.projectDetail?.dxf_file]);

  const toggleDxf = () => {
    setShowDxf(!showDxf);
  };

  const togglePhotoshotVisiblity = () => {
    setShowPhotoshot(!showPhotoshot);
    viewer.setPhotoshotVisible(!showPhotoshot);
  };

  // const disabledToggleAxis = () => {
  //   return whilePointCloudLoading;
  // }

  const toggleShowPointcloud = () => {
    setShowPointCloud(!showPointCloud);
    viewer.setPointCloudVisibility(!showPointCloud);
  };

  const toggleProjectState = () => {
    // hideAllMenus();
    setOpenProjectState(!openProjectState);
  };

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [imageInfo, setImageInfo] = useState<{ name: string; src: string }>({
    name: "",
    src: "",
  });

  useEffect(() => {
    const handlePhotoshotClick = (e: any) => {
      const { photoName } = e.detail;
      let hostName = config.serverHost.includes("localhost")
        ? "http://localhost:9090/quick3d"
        : config.serverHost;
      const imageUrl = `${hostName}/project_${props.projectDetail.id}/${photoName}`;
      setImageInfo({ name: photoName, src: imageUrl });
      setOpenImageDialog(true);
    };

    window.addEventListener("photoshot-clicked", handlePhotoshotClick);
    return () => {
      window.removeEventListener("photoshot-clicked", handlePhotoshotClick);
    };
  }, []);

  useEffect(() => {
    const unobserveDesign = observe(viewer, ["designStatus"], () => {
      devLog("ShowToolMenu.designStatus", { status: viewer.designStatus });
      setOldDesignLoading(viewer.designStatus === "loading");
    });
    const unobserveScm = observe(viewer, ["scmStatus"], () => {
      devLog("ShowToolMenu.scmStatus", { status: viewer.scmStatus });
      setModel3DLoading(viewer.scmStatus === "loading");
    });

    return () => {
      unobserveDesign && unobserveDesign();
      unobserveScm && unobserveScm();
    };
  }, [viewer]);

  // Must be subscribed inside an effect: called from the render body, observe()
  // appended a new callback to viewer.onChange on every single render and never
  // unsubscribed it.
  useEffect(() => {
    const unobserveDxf = observe(viewer, ["dxfStatus"], () => {
      setDxfLoading(viewer.dxfStatus === "loading");
    });
    const unobserveGsplat = observe(viewer, ["gsplatStatus"], () => {
      setGsplatLoading(viewer.gsplatStatus === "loading");
    });
    setDxfLoading(viewer.dxfStatus === "loading");
    setGsplatLoading(viewer.gsplatStatus === "loading");

    return () => {
      unobserveDxf && unobserveDxf();
      unobserveGsplat && unobserveGsplat();
    };
  }, [viewer]);

  return (
    <>
      <Q3DIconButton
        title={t("SHOW_HIDE")}
        titleType="tooltip"
        onClick={(e) => {
          openVisibilityMenu(e);
        }}
        icon={
          <SvgIcon>
            <path d={toolBoxIcon_1} fill={BLACK} />
            <path d={toolBoxIcon_2} fill={WHITE} />
            <path d={toolBoxIcon_3} fill={WHITE} />
          </SvgIcon>
        }
        height={btnSize}
        width={btnSize}
        disabled={detailTab === DETAIL_TAB.PHOTO}
      />
      <Menu
        id="simple-menu"
        keepMounted
        anchorEl={visibilityAnchorEl}
        open={Boolean(visibilityAnchorEl)}
        onClose={closeVisibilityMenu}
        style={{ borderRadius: "0px" }}
        // PaperProps={{ style: { ...customBackground, ...customColor } }}
      >
        {((!isArView && detailTab !== DETAIL_TAB.PHOTO) ||
          (isArView && projectDetail.las_file !== null)) && (
          <MenuItem
            value="focusBelow"
            className="point-view4"
            // disabled={disabledToggleAxis()}
            onClick={toggleShowPointcloud}
          >
            <Tooltip
              title={
                <span className="point-view4-tool-tip">
                  {t("DISPLAY_POINT_GROUP")}
                </span>
              }
              arrow
              placement="left"
              disableInteractive
            >
              <span>
                <SvgIcon>
                  <path d={toolBoxIcon_1} fill={BLACK} />
                  <path d={toolBoxIcon_2} fill={WHITE} />
                  <path d={toolBoxIcon_3} fill={WHITE} />
                </SvgIcon>
              </span>
            </Tooltip>
          </MenuItem>
        )}
        <MenuItem
          value="focusBelow"
          className="point-view4"
          // disabled={disabledToggleAxis()}
          onClick={toggleAxisVisiblity}
        >
          <Tooltip
            title={
              <span className="point-view4-tool-tip">{t("DISPLAY_AXIS")}</span>
            }
            arrow
            placement="left"
            disableInteractive
          >
            <span>
              <ToolIcon
                imagePath={CoordinateIcon}
                color={
                  // disabledToggleAxis() || !showAxis ? "gray" : customColor.color
                  customColor.color
                }
              />
            </span>
          </Tooltip>
        </MenuItem>
        {!isArView && (
          <MenuItem onClick={toggleProjectState}>
            <Tooltip
              title={
                <span className="point-view4-tool-tip">
                  {t("DISPLAY_STATUS")}
                </span>
              }
              arrow
              placement="left"
              disableInteractive
            >
              <FactCheckIcon
                sx={{
                  color: customColor.color,
                }}
              />
            </Tooltip>
          </MenuItem>
        )}
        {!isArView && (
          <MenuItem onClick={togglePhotoshotVisiblity}>
            <Tooltip
              title={
                <span className="point-view4-tool-tip">
                  {t("DISPLAY_PHOTOSHOT")}
                </span>
              }
              arrow
              placement="left"
              disableInteractive
            >
              <span>
                <RouteIcon
                  style={{
                    transform: "rotate(45deg)",
                  }}
                />
              </span>
            </Tooltip>
          </MenuItem>
        )}

        {/* Old group — IFC / LandXML */}
        <MenuItem
          aria-label={t("DISPLAY_DESIGN_DATA_IFC")}
          onClick={toggleOldDesign}
          disabled={oldDesignLoading || !canLoadOldDesign(projectDetail)}
        >
          <Tooltip
            title={
              <span className="point-view4-tool-tip">
                {t("DISPLAY_DESIGN_DATA_IFC")}
              </span>
            }
            arrow
            placement="left"
            disableInteractive
          >
            <span style={{ marginLeft: "3px" }}>
              <ToolIcon
                imagePath={DataDesign}
                color={
                  !canLoadOldDesign(projectDetail) ? "gray" : customColor.color
                }
                width={22}
                height={22}
              />
              {oldDesignLoading && (
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    top: "0",
                    left: "0",
                    backgroundColor: "rgba(255,255,255,0.5)",
                  }}
                >
                  <CircularProgress size={30} />
                </div>
              )}
            </span>
          </Tooltip>
        </MenuItem>

        {/* Model-3D group — FBX / GLB / DWG (converted to SCM) */}
        <MenuItem
          aria-label={t("DISPLAY_DESIGN_DATA_MODEL3D")}
          title={model3DStatusMessage}
          onClick={toggleModel3D}
          disabled={model3DLoading || !canShowModel3D(projectDetail)}
        >
          <Tooltip
            title={
              <span className="point-view4-tool-tip">
                {model3DStatusMessage}
              </span>
            }
            arrow
            placement="left"
            disableInteractive
          >
            <span style={{ marginLeft: "3px" }}>
              <ToolIcon
                imagePath={DataDesign}
                color={
                  !canShowModel3D(projectDetail) ? "gray" : customColor.color
                }
                width={22}
                height={22}
              />
              {model3DLoading && (
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    top: "0",
                    left: "0",
                    backgroundColor: "rgba(255,255,255,0.5)",
                  }}
                >
                  <CircularProgress size={30} />
                </div>
              )}
            </span>
          </Tooltip>
        </MenuItem>
        {!!projectDetail?.dxf_file && (
          <MenuItem
            onClick={toggleDxf}
            disabled={detailTab === DETAIL_TAB.PHOTO || dxfLoading}
          >
            <Tooltip
              title={
                <span className="point-view4-tool-tip">
                  {t("SHOW_HIDE_DXF")}
                </span>
              }
              arrow
              placement="left"
              disableInteractive
            >
              <span style={{ position: "relative", display: "inline-flex" }}>
                <LinearScaleIcon
                  sx={{
                    color: customColor.color,
                  }}
                />
                {dxfLoading && (
                  <CircularProgress
                    size={20}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-10px",
                      marginLeft: "-10px",
                      color: customColor.color,
                    }}
                  />
                )}
              </span>
            </Tooltip>
          </MenuItem>
        )}
        <MenuItem
          disabled={!projectDetail.gsx_file || gsplatLoading}
          onClick={() => {
            if (projectDetail.gsx_file) {
              setShowGsplat(!showGsplat);
            }
          }}
        >
          <Tooltip
            title={
              <span className="point-view4-tool-tip">
                {t("DISPLAY_SPLATTING_GAUSSIAN")}
              </span>
            }
            arrow
            placement="left"
            disableInteractive
          >
            <span style={{ position: "relative", display: "inline-flex" }}>
              <GrainIcon
                sx={{
                  width: 24,
                  height: 24,
                  color:
                    !projectDetail.gsx_file || !showGsplat
                      ? "gray"
                      : customColor.color,
                }}
              />
              {gsplatLoading && (
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    top: "0",
                    left: "0",
                    backgroundColor: "rgba(255,255,255,0.5)",
                  }}
                >
                  <CircularProgress size={30} />
                </div>
              )}
            </span>
          </Tooltip>
        </MenuItem>
      </Menu>
      {showPhotoshot && (
        <ImageViewerDialog
          open={openImageDialog}
          setOpen={setOpenImageDialog}
          imageSrc={imageInfo.src}
          imageName={imageInfo.name}
        />
      )}
    </>
  );
};
